import '../base';

import { Section } from "../tools/section";
import { Accordion } from "../objects/accordion/Accordion";
import { initLineAnimations } from "../animations/line-animation";
import AOS from 'aos';

const SELECTOR_IMAGE_VIDEO_ACCORDIONS = '[data-image-video-accordions]';
const SELECTOR_ACCORDION = '[data-accordion]';
const SELECTOR_ACCORDION_ITEMS = '[data-accordion-item]';
const SELECTOR_MEDIA_WRAPPER = '[data-media-wrapper]';
const SELECTOR_MEDIA_ITEMS = '[data-media-item]';

class ImageVideoAccordionsSection extends Section {
  private accordion: Accordion | null = null;
  private mediaWrapper: HTMLElement | null = null;
  private accordionItems: NodeListOf<HTMLElement> | null = null;

  queryChildren(container: HTMLElement): void {
    // Find and initialize accordion container
    const accordionElement = container.querySelector<HTMLElement>(SELECTOR_ACCORDION);
    if (accordionElement) {
      this.accordion = new Accordion(accordionElement);
    }

    // Find media wrapper and accordion items
    this.mediaWrapper = container.querySelector<HTMLElement>(SELECTOR_MEDIA_WRAPPER);
    this.accordionItems = container.querySelectorAll<HTMLElement>(SELECTOR_ACCORDION_ITEMS);
  }

  attachEvents(container: HTMLElement): void {
    // Initialize AOS for staggered animations
    AOS.init({ 
      duration: 800, 
      easing: 'ease-out', 
      once: true 
    });

    // Apply staggered animation to accordion items
    this.applyAOSStagger(container);

    // Initialize line animations
    initLineAnimations(container);

    // Add accordion click handlers for media switching
    this.attachAccordionMediaHandlers(container);

    // Ensure first media item is active on initialization
    this.initializeFirstMedia();

    // Accordion is automatically initialized in constructor, no additional call needed
  }

  detatchEvents(): void {
    // Clean up accordion events
    if (this.accordion) {
      this.accordion.detachEvents();
    }
  }

  private attachAccordionMediaHandlers(container: HTMLElement): void {
    if (!this.accordionItems || !this.mediaWrapper) return;

    this.accordionItems.forEach((accordionItem, index) => {
      const accordionHeading = accordionItem.querySelector<HTMLElement>('.o-accordion__heading');
      if (accordionHeading) {
        accordionHeading.addEventListener('click', () => {
          this.switchMediaForAccordion(accordionItem, index);
        });
      }
    });
  }

  private switchMediaForAccordion(accordionItem: HTMLElement, index: number): void {
    if (!this.mediaWrapper) return;

    const blockId = accordionItem.dataset.accordionMedia;
    
    if (!blockId) {
      return;
    }

    // Hide all media items
    const allMediaItems = this.mediaWrapper.querySelectorAll<HTMLElement>(SELECTOR_MEDIA_ITEMS);
    
    allMediaItems.forEach(item => {
      item.classList.remove('is-active');
    });

    // Show the media item for this accordion
    const targetMediaItem = this.mediaWrapper.querySelector<HTMLElement>(`[data-accordion-media="${blockId}"]`);
    
    if (targetMediaItem) {
      targetMediaItem.classList.add('is-active');
    } else {
      // Fallback: show the first media item if no match found
      if (allMediaItems.length > 0) {
        allMediaItems[0].classList.add('is-active');
      }
    }
  }

  private initializeFirstMedia(): void {
    if (!this.mediaWrapper) return;

    const allMediaItems = this.mediaWrapper.querySelectorAll<HTMLElement>(SELECTOR_MEDIA_ITEMS);
    
    // Ensure at least one media item is active
    let hasActiveItem = false;
    allMediaItems.forEach(item => {
      if (item.classList.contains('is-active')) {
        hasActiveItem = true;
      }
    });
    
    if (!hasActiveItem && allMediaItems.length > 0) {
      allMediaItems[0].classList.add('is-active');
    }
  }

  private applyAOSStagger(container: HTMLElement): void {
    const accordionItems = container.querySelectorAll<HTMLElement>(SELECTOR_ACCORDION_ITEMS);
    accordionItems.forEach((el, idx) => {
      const delay = (idx * 100) + 400; // Start after header elements (300ms) + stagger
      // Ensure required attributes exist
      el.dataset.aos = el.dataset.aos || 'fade-up';
      el.dataset.aosOnce = 'true';
      el.setAttribute('data-aos-delay', String(delay));
      // Force the delay even if AOS doesn't re-read attributes
      el.style.transitionDelay = `${delay}ms`;
      el.style.animationDelay = `${delay}ms`;
    });
  }
}

Section.register(SELECTOR_IMAGE_VIDEO_ACCORDIONS, ImageVideoAccordionsSection);
